import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EditPasswordFormData } from '@jwp/ott-common/types/account';
import type { FormErrors } from '@jwp/ott-common/types/form';
import { testId } from '@jwp/ott-common/src/utils/common';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import PasswordField from '../PasswordField/PasswordField';
import TextField from '../TextField/TextField';
import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import { useTheme } from '../../../../../ThemeContext';

import styles from './EditPasswordForm.module.scss';


type Props = {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  setValue?: (key: keyof EditPasswordFormData, value: string) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string;
  errors: FormErrors<EditPasswordFormData>;
  value: EditPasswordFormData;
  validationError?: boolean;
  submitting: boolean;
  showOldPasswordField?: boolean;
  showResetTokenField?: boolean;
  email?: string;
  onResendEmailClick?: () => void;
};

const EditPasswordForm: React.FC<Props> = ({
  onSubmit,
  onChange,
  onBlur,
  showOldPasswordField,
  showResetTokenField,
  value,
  errors,
  submitting,
  validationError,
  email,
  onResendEmailClick,
}: Props) => {
  const { t } = useTranslation(['account', 'user']);
  const { universalStyles } = useTheme();
  const { isCleeng } = useAppConfig();
  return (
    <form onSubmit={onSubmit} data-testid={testId('forgot-password-form')} noValidate className={styles.forgotPasswordForm}>
      {errors.form && (
        <FormFeedback variant="error" visible={!validationError}>
          {errors.form}
        </FormFeedback>
      )}
      <h2 className={styles.title} style={universalStyles.primaryTextColor}>
        {showOldPasswordField && showResetTokenField ? t('user:account.add_password') : t('reset.password_reset')}
      </h2>
      {showOldPasswordField && showResetTokenField && (
        <p className={styles.paragraph}>
          {t('user:account.add_password_modal_text', { email })}
          <a className={styles.resendLink} onClick={onResendEmailClick}>
            {t('user:account.resend_mail')}
          </a>
        </p>
      )}
      {showOldPasswordField && !showResetTokenField && (
        <PasswordField
          value={value.oldPassword}
          onChange={onChange}
          onBlur={onBlur}
          label={t('reset.old_password')}
          placeholder={t('reset.old_password')}
          error={!!errors.oldPassword}
          name="oldPassword"
          showToggleView={false}
          showHelperText={false}
          required
        />
      )}
      {!isCleeng && (
        <TextField
          className={styles.textField}
          value={value.resetPasswordToken || ''}
          onChange={onChange}
          onBlur={onBlur}
          label={t('reset.reset_password_token')}
          placeholder={t('reset.token_placeholder')}
          name="resetPasswordToken"
          type="text"
          autoComplete=""
          required
          customStyle={universalStyles.emailIdText}
        />
      )}

      <PasswordField
        value={value.password}
        onChange={onChange}
        onBlur={onBlur}
        label={t('reset.new_password')}
        placeholder={t('reset.new_password_placeholder')}
        error={!!errors.password}
        name="password"
        required
      />

      <PasswordField
        value={value.passwordConfirmation}
        onChange={onChange}
        onBlur={onBlur}
        label={t('reset.confirm_password')}
        placeholder={t('reset.confirm_password_placeholder')}
        error={!!errors.passwordConfirmation}
        name="passwordConfirmation"
        required
      />

      <Button
        type="submit"
        className={styles.button}
        fullWidth
        color="primary"
        size="exlarge"
        disabled={submitting}
        label={t('reset.reset_password_button')}
        textStyle={universalStyles.primaryThemeButtonText}
        style={universalStyles.signUpButton}
      />
      {submitting && <LoadingOverlay transparentBackground inline />}
    </form>
  );
};

export default EditPasswordForm;
